import React from "react";
import { MessageSquareReply } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Stars, photoSrc } from "@/components/Stars";
import PhotographerBadge from "@/components/PhotographerBadge";
import ReportButton from "@/components/ReportButton";

const LANG_FLAG = { ar: "🇸🇦", en: "🇬🇧", it: "🇮🇹", fr: "🇫🇷" };

function ReviewItem({ r }) {
  const { lang } = useLang();
  return (
    <li className="flex gap-3" data-testid={`review-${r.id}`}>
      {r.author.picture ? (
        <img src={r.author.picture} alt="" className="w-8 h-8 rounded-full shrink-0" referrerPolicy="no-referrer" />
      ) : (
        <span className="w-8 h-8 rounded-full bg-sandDeep shrink-0 flex items-center justify-center text-xs font-bold">{r.author.name?.[0]}</span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-ink">{r.author.name}</span>
          <PhotographerBadge show={r.is_photographer} />
          <Stars value={r.rating} size="w-3 h-3" />
          <span className="text-xs" title={r.lang}>{LANG_FLAG[r.lang]}</span>
          <span className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString(lang === "ar" ? "ar-EG-u-nu-latn" : lang)}</span>
          <span className="ms-auto"><ReportButton item={r} /></span>
        </div>
        <p className="text-sm text-ink/85 leading-snug mt-0.5" dir="auto">{r.text}</p>
        {r.video_url && (
          <video src={photoSrc(r.video_url)} poster={photoSrc(r.video_poster_url)} controls preload="none" playsInline
            className="block mt-2 h-32 rounded-lg border border-stoneLine bg-black" data-testid={`review-video-${r.id}`} />
        )}
        {r.photo_url && (
          <a href={photoSrc(r.photo_url)} target="_blank" rel="noopener noreferrer" className="block mt-2 w-fit" data-testid={`review-photo-${r.id}`}>
            <img src={photoSrc(r.photo_url)} alt="" loading="lazy" className="h-24 rounded-lg border border-stoneLine object-cover hover:opacity-90 transition-opacity" />
          </a>
        )}
        {r.admin_reply && <AdminReplyNote reply={r.admin_reply} lang={lang} id={r.id} />}
      </div>
    </li>
  );
}

function AdminReplyNote({ reply, lang, id }) {
  const { t } = useLang();
  return (
    <div className="mt-2 ms-2 ps-3 border-s-2 border-ochre/50 bg-ochre/5 rounded-e-lg py-2 pe-3" data-testid={`review-reply-${id}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-ochre font-semibold">
        <MessageSquareReply className="w-3 h-3" /> {t("admin_reply_label")}
        <span className="text-muted-foreground normal-case tracking-normal">· {new Date(reply.at).toLocaleDateString(lang === "ar" ? "ar-EG-u-nu-latn" : lang)}</span>
      </div>
      <p className="text-sm text-ink/85 leading-snug mt-0.5" dir="auto">{reply.text}</p>
    </div>
  );
}

export default function ReviewList({ reviews }) {
  if (!reviews.length) return null;
  return (
    <ul className="space-y-3" data-testid="review-list">
      {reviews.map((r) => <ReviewItem key={r.id} r={r} />)}
    </ul>
  );
}
